import type {
    ClassicalAPITrackMetadataArtist,
    ClassicalAPITrackMetadataCollection,
    ClassicalAPITrackMetadataCredit,
} from '@jet-app/music-classical/api/types';
import { fixArtwork } from '@jet-app/music-classical/models';
import { createFlowActionForAction } from '@jet-app/music-classical/utilities/create-flow-action';
import type { ObjectGraph } from '@jet/environment/dependencies';
import { makeContextualMenuFetchAction } from '@jet-app/music-classical/action-model/contextual-menu/FetchAction';
import type { MediaItem } from '@amp/musickit';
import type { QueueItemMetadata, QueueItemMetadataMap } from './types';
import type { CreditItem } from '@jet-app/music-classical/utilities/transforms/info';

/**
 * Utility that converts a MediaItem to QueueItemMetadata
 * Used when the queue includes a track that the Classical API doesn't know about.
 * Returns a very limited amount of data compared to what we get from Presto.
 *
 * @method transformMediaItem
 * @param {MediaItem} item
 * @returns {QueueItemMetadata}
 */
export function transformMediaItem(
    mediaItem: MediaItem,
): QueueItemMetadataMap | Object {
    const { attributes, id } = mediaItem;

    return {
        artwork: fixArtwork(attributes.artwork),
        albumName: attributes.albumName,
        artistName: attributes.artistName,
        // audioTraits: attributes.audioTraits,
        composerName: attributes.composerName,
        durationInMillis: attributes.durationInMillis,
        id,
        movementName: attributes.movementName,
        title: attributes.name,
        // type: mediaItem.type,
        // url: attributes.url, // This ends up being Music's album url
        workName: attributes.workName,
    };
}

export function transformTrackMetadata(
    metadataCollection: ClassicalAPITrackMetadataCollection,
    queue: Array<unknown>,
    trackIds: string[],
    objectGraph: ObjectGraph,
): QueueItemMetadataMap | Object {
    const newCollection = {};
    // Map of queue items in the event that a queue item exists that
    // the Classical API doesn't know about.
    const queueMap = new Map(
        queue.map((mediaItem: QueueItemMetadata) => [mediaItem.id, mediaItem]),
    );

    trackIds.forEach((id) => {
        const metadata = metadataCollection[id];

        if (!metadata) {
            // Queue item does not exist in the Classical API
            newCollection[id] = transformMediaItem(queueMap.get(id));
        } else {
            newCollection[id] = {
                // additionalAuthors: metadata.additionalAuthors,
                albumAction: createFlowActionForAction(
                    metadata.albumAction,
                    objectGraph,
                ),
                artwork: fixArtwork(metadata.albumArt),
                albumName: metadata.albumTitle,
                artistName: metadata.displayArtists,
                artists: transformMetaArtists(metadata.artists, objectGraph),
                // audioTraits: metadata.audioTraits,
                // bookletButton: metadata.bookletButton,
                composerAction: createFlowActionForAction(
                    metadata.composerAction,
                    objectGraph,
                ),
                composerName: metadata.composerTitle,
                composerShortName: metadata.composerShortTitle,
                contextMenuAction: makeContextualMenuFetchAction(
                    metadata.trackMenuDataUrl,
                ),
                credits: transformMetaCredits(metadata.credits),
                duration: metadata.duration,
                durationInMillis: metadata.durationMilliseconds,
                header: metadata.header,
                id: metadata.id,
                // location: metadata.location,
                movementHeading: metadata.movementHeading,
                movementName: metadata.movementOrTrackTitle,
                // offline: metadata.offline,
                // screenType: metadata.screenType,
                title: metadata.title,
                // type: metadata.type,
                // url: metadata.url, // This ends up being the album API query url
                workAction: createFlowActionForAction(
                    metadata.workAction,
                    objectGraph,
                ),
                workName: metadata.workTitle,
                workOrAlbumShortTitle: metadata.workOrAlbumShortTitle,
                workOrTrackTitleGroup: metadata.workOrTrackTitleGroup,
                workSubtitle: metadata.workSubtitle,
            };
        }
    });

    return newCollection;
}

// Has a CreditItem[] type for use in info panels, but can also be used
// to pass into SeparatedLinks component within other components
function transformMetaArtists(
    artists: ClassicalAPITrackMetadataArtist[],
    objectGraph: ObjectGraph,
): CreditItem[] {
    return artists.map((artist) => {
        return {
            title: artist.role,
            subtitle: artist.title,
            // shortTitle: artist.shortTitle,
            segue: !!artist.action
                ? createFlowActionForAction(artist.action, objectGraph)
                : undefined,
            type: 'info',
        };
    });
}

function transformMetaCredits(
    credits: ClassicalAPITrackMetadataCredit[],
): CreditItem[] {
    return credits.map((credit) => {
        return {
            title: credit.credits,
            subtitle: credit.party,
            type: 'info',
        };
    });
}
